<script setup lang="ts">
import { ref, useSlots } from 'vue';
import DragOverlay from './DragOverlay.vue';
import FileDropChoiceModal from './FileDropChoiceModal.vue';
import { useFileDrop } from './useFileDrop';
import type { FileDropAction } from './actions/types';

interface Props {
  /** The actions available for files dropped on this page. */
  actions: FileDropAction[];
}

const props = defineProps<Props>();

const slots = useSlots();

const draggedFiles = ref<File[]>([]);
const showChoice = ref(false);
const activeAction = ref<FileDropAction | null>(null);
// Actions available for the current drop (after permission / file-count filtering).
const choiceActions = ref<FileDropAction[]>([]);

const reset = (): void => {
  draggedFiles.value = [];
  showChoice.value = false;
  activeAction.value = null;
  choiceActions.value = [];
};

const runAction = (action: FileDropAction): void => {
  if (action.component) {
    // Complex flow: mount the action's own component with the files.
    activeAction.value = action;
  } else if (action.run) {
    void action.run(draggedFiles.value, { close: reset });
  }
};

const handleChoice = (action: FileDropAction): void => {
  showChoice.value = false;
  runAction(action);
};

const { isDragging } = useFileDrop(files => {
  draggedFiles.value = files;

  // Only offer actions allowed for this drop (permissions + file count).
  const available = props.actions.filter(action =>
    action.isAvailable ? action.isAvailable(files) : true
  );

  if (available.length === 1) {
    // Single available action — go straight into it, no choice needed.
    runAction(available[0]);
  } else if (available.length > 1) {
    choiceActions.value = available;
    showChoice.value = true;
  }
});
</script>

<template>
  <slot />

  <!-- Drag-time visual: only when the host supplies an `overlay` slot. -->
  <DragOverlay v-if="slots.overlay" :is-dragging="!!isDragging">
    <slot name="overlay" />
  </DragOverlay>

  <FileDropChoiceModal
    v-model:show="showChoice"
    :actions="choiceActions"
    :files="draggedFiles"
    @select="handleChoice"
  />

  <!-- Active action's self-contained flow (e.g. the start-review modal). -->
  <component
    :is="activeAction.component"
    v-if="activeAction?.component"
    :files="draggedFiles"
    @done="reset"
  />
</template>
